import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { commitmentKeys, projectContractorKeys, taskKeys } from "@/lib/query";

interface ProjectContractor {
  id: string;
  projectId: string;
  contractorId: string;
  contactId: string | null;
  aiaDocReceived: boolean;
  lienWaiverReceived: boolean;
  createdAt: string;
  companyName: string | null;
  trade: string | null;
  tradeCategoryId: string | null;
  tradeCategoryName: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactTitle: string | null;
}

interface ContractorTask {
  id: string;
  projectId: string;
  title: string | null;
  description: string | null;
  taskType: string | null;
  phase: string | null;
  status: string | null;
  dueDate: string | null;
  assignedToUserId: string | null;
  assignedToCompanyId: string | null;
  assignedToContactId: string | null;
  displayOrder: number | null;
}

interface ContractorCommitment {
  id: string;
  commitmentNumber: string | null;
  commitmentType: string | null;
  title: string | null;
  status: string | null;
  originalAmount: string | null;
  revisedAmount: string | null;
  totalBilledToDate: string | null;
  companyName: string | null;
}

export type { ContractorCommitment, ContractorTask, ProjectContractor };

export function useProjectContractors(projectId: string) {
  return useQuery({
    queryKey: projectContractorKeys.project(projectId),
    queryFn: () => apiFetch<ProjectContractor[]>(`/projects/${projectId}/contractors`),
    enabled: !!projectId,
  });
}

export function useAddProjectContractor(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contractorId: string) =>
      apiPost<{ data: ProjectContractor }>(`/projects/${projectId}/contractors`, { contractorId }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: projectContractorKeys.project(projectId),
      });
      toast.success("Contractor assigned to project");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveProjectContractor(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/projects/${projectId}/contractors/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: projectContractorKeys.project(projectId),
      });
      toast.success("Contractor removed from project");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProjectContractor(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      aiaDocReceived?: boolean;
      lienWaiverReceived?: boolean;
      contactId?: string | null;
    }) => apiPatch<{ data: ProjectContractor }>(`/projects/${projectId}/contractors/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: projectContractorKeys.project(projectId),
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useContractorTasks(projectId: string, contractorId: string, enabled = false) {
  return useQuery({
    queryKey: projectContractorKeys.tasks(projectId, contractorId),
    queryFn: () =>
      apiFetch<ContractorTask[]>(`/projects/${projectId}/contractors/${contractorId}/tasks`),
    enabled: enabled && !!projectId && !!contractorId,
  });
}

export function useContractorCommitments(projectId: string, contractorId: string, enabled = false) {
  return useQuery({
    queryKey: projectContractorKeys.commitments(projectId, contractorId),
    queryFn: () =>
      apiFetch<ContractorCommitment[]>(
        `/projects/${projectId}/contractors/${contractorId}/commitments`,
      ),
    enabled: enabled && !!projectId && !!contractorId,
  });
}

// Re-export task/commitment key invalidation helpers used by other mutations
export { commitmentKeys, taskKeys };
